import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type {
  GetBatteryHistoryQuery,
  GetBatteryHistoryResponseBody,
  GetMecaHistoryQuery,
  GetMecaHistoryResponseBody,
  Valve,
} from "../types/valve.type";

type MecaHistoryFilters = Omit<GetMecaHistoryQuery, "cursor">;
type BatteryHistoryFilters = Omit<GetBatteryHistoryQuery, "cursor">;

export const createValveQueries = (api: CoreApiClient) => {
  const { client } = api;

  const list = async (buildingId: string): Promise<Valve[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/valves`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const mecaHistory = async (
    buildingId: string,
    valveId: string,
    filters: MecaHistoryFilters,
    cursor?: string,
  ): Promise<GetMecaHistoryResponseBody> => {
    const { data, error } = await client.GET("/buildings/{buildingId}/valves/{valveId}/meca-history", {
      params: { path: { buildingId, valveId }, query: { ...filters, cursor } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const batteryHistory = async (
    buildingId: string,
    valveId: string,
    filters: BatteryHistoryFilters,
    cursor?: string,
  ): Promise<GetBatteryHistoryResponseBody> => {
    const { data, error } = await client.GET("/buildings/{buildingId}/valves/{valveId}/battery-history", {
      params: { path: { buildingId, valveId }, query: { ...filters, cursor } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  return {
    list,
    mecaHistory,
    batteryHistory,
    queries: createQueryKeys("valve", {
      building: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => list(buildingId),
      }),
      mecaHistory: (buildingId: string, valveId: string, filters: MecaHistoryFilters) => ({
        queryKey: [buildingId, valveId, filters],
        queryFn: ({ pageParam }: { pageParam?: string }) => mecaHistory(buildingId, valveId, filters, pageParam),
      }),
      batteryHistory: (buildingId: string, valveId: string, filters: BatteryHistoryFilters) => ({
        queryKey: [buildingId, valveId, filters],
        queryFn: ({ pageParam }: { pageParam?: string }) => batteryHistory(buildingId, valveId, filters, pageParam),
      }),
    }),
  };
};

export type { BatteryHistoryFilters, MecaHistoryFilters };
